import React from 'react';
import TrophySvg from './TrophySvg';
import { matchScoreDisplay } from '../utils/matchScore';

function CompetitionScores({ approveScore, completedMatches, isAdmin, isFinalised, isPlayerInMatch, leaderboard, matches, refreshComp, selectedComp, setActiveStep, setScoringMatch, showConfirm, totalPlayable }) {
  return (
    <div className="w3-card w3-round w3-white w3-margin-top">
      <div className="w3-container w3-padding">
        <h4><i className="fa fa-pencil w3-text-theme w3-margin-right"></i>Score Entry</h4>

        {matches.length === 0 ? (
          <p className="w3-text-grey w3-center"><em>Generate fixtures first</em></p>
        ) : (
          <>
            {/* Progress bar */}
            <div className="w3-light-grey w3-round" style={{ height: '24px', marginBottom: '16px' }}>
              <div className="w3-green w3-round" style={{
                height: '24px', width: totalPlayable > 0 ? `${(completedMatches / totalPlayable) * 100}%` : '0%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 'var(--gs-text-sm)', fontWeight: 700, color: '#fff',
                minWidth: completedMatches > 0 ? '50px' : '0',
              }}>
                {completedMatches}/{totalPlayable}
              </div>
            </div>

            {/* Pending matches first, then completed */}
            {(() => {
              const tt = selectedComp.tournamentType || 'round-robin';
              const isScoresDE = tt === 'double-elimination';
              const isElimination = tt === 'double-elimination' || tt === 'knockout';

              const pending = matches.filter(m => {
                if (m.completed || m.result === 'bye') return false;
                // In DE, show one-team waiting matches so the waiting finalist bye state is visible.
                if (isScoresDE) return !!(m.homeTeamId || m.awayTeamId);
                return !!(m.homeTeamId && m.awayTeamId);
              });
              const done = matches.filter(m => m.completed && m.result !== 'bye' && m.result !== 'not-played');
              // For DE, include bye matches so the bracket columns show the full round progression
              const byeMatches = isScoresDE ? matches.filter(m => m.result === 'bye' && (m.homeTeamId || m.awayTeamId)) : [];
              const displayMatches = [...pending, ...done, ...byeMatches];

              const deRoundLabel = (m) => {
                const br = m.bracketRound != null ? m.bracketRound : m.round;
                if (m.bracketType === 'winners') return `Winners Round ${br}`;
                if (m.bracketType === 'losers') return `Losers Round ${br}`;
                if (m.bracketType === 'grand-final') return 'Grand Final';
                if (m.bracketType === 'decider') return 'Decider';
                if (isScoresDE) return `Winners Round ${br}`;
                return `Round ${m.round}`;
              };

              const cardShellStyle = {
                borderRadius: '12px',
                marginBottom: '10px',
                border: '1px solid #d7e1e8',
                overflow: 'hidden',
                minHeight: '78px',
              };

              const renderScoreCard = (m) => {
                const hasBothTeams = !!(m.homeTeamId && m.awayTeamId);
                const canScore = hasBothTeams && ((isAdmin && !isFinalised) || (!isAdmin && !isFinalised && isPlayerInMatch(m)));
                const isPending = !m.completed;
                const isOneTeamWaiting = isScoresDE && isPending && !hasBothTeams && m.result !== 'bye';
                const needsApproval = m.completed && !m.scoreApproved && m.scoreSubmittedBy === 'player';
                const cardColor = isPending
                  ? 'white'
                  : needsApproval ? '#e7f2ff'
                  : m.result === 'not-played' ? '#fff5cf'
                  : (m.completed && m.homeScore === 0 && m.awayScore === 0 && (m.result === 'win-home' || m.result === 'win-away')) ? '#fff0db'
                  : '#e9f9e5';

                  if (m.result === 'bye') {
                    const advTeam = m.HomeTeam ? m.HomeTeam.name : (m.AwayTeam ? m.AwayTeam.name : 'Team');
                    return (
                      <div key={m.id} className="w3-card" style={{ ...cardShellStyle, background: '#f4f4f4', opacity: 0.8 }}>
                        <div style={{ padding: '8px 10px', background: '#888', color: 'white', display: 'flex', justifyContent: 'space-between', gap: '8px', fontSize: 'var(--gs-text-sm)' }}>
                          <span>{isScoresDE ? deRoundLabel(m) : `Round ${m.round}`}</span>
                          <span>Rink {m.rink || '-'}</span>
                        </div>
                        <div className="w3-padding-small">
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', gap: '8px', margin: '4px 0 8px' }}>
                            <strong className="w3-text-grey" style={{ textAlign: 'right' }}>{advTeam}</strong>
                            <span className="w3-tag w3-round w3-small w3-light-grey">BYE</span>
                            <strong className="w3-text-grey">—</strong>
                          </div>
                          <div className="w3-center w3-tiny w3-text-grey"><i className="fa fa-arrow-right w3-margin-right"></i>Advances automatically</div>
                        </div>
                      </div>
                    );
                  }

                return (
                  <div key={m.id}
                    className={`w3-card ${canScore ? 'w3-hover-shadow' : ''}`}
                    onClick={() => setScoringMatch(m)}
                    style={{ ...cardShellStyle, cursor: 'pointer', opacity: isFinalised ? 0.75 : 1, background: cardColor }}>
                    <div style={{ padding: '8px 10px', background: '#2f3f4a', color: 'white', display: 'flex', justifyContent: 'space-between', gap: '8px', fontSize: 'var(--gs-text-sm)' }}>
                      <span>{isScoresDE ? deRoundLabel(m) : `Round ${m.round}`}</span>
                      <span>Rink {m.rink || '-'}</span>
                    </div>
                    <div className="w3-padding-small">
                      {m.result === 'not-played' ? (
                        <div className="w3-center" style={{ padding: '8px 0' }}>
                          <span className="w3-tag w3-round w3-orange w3-small"><i className="fa fa-ban w3-margin-right"></i>Not Played</span>
                        </div>
                      ) : (m.completed && m.homeScore === 0 && m.awayScore === 0 && (m.result === 'win-home' || m.result === 'win-away')) ? (
                        <div className="w3-center" style={{ padding: '8px 0' }}>
                          <span className="w3-tag w3-round w3-deep-orange w3-small"><i className="fa fa-flag w3-margin-right"></i>Forfeit</span>
                        </div>
                      ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', gap: '8px', margin: '4px 0 8px' }}>
                          <strong className={m.result === 'win-home' ? 'w3-text-green' : 'w3-text-theme'} style={{ textAlign: 'right' }}>{m.HomeTeam ? m.HomeTeam.name : 'BYE'}</strong>
                          <span className={`w3-tag w3-round w3-small ${isPending ? 'w3-amber' : 'w3-light-grey'}`}>
                            {isOneTeamWaiting ? 'BYE' : (isPending ? 'vs' : (() => { const sc = matchScoreDisplay(m, selectedComp); return `${sc.home} - ${sc.away}`; })())}
                          </span>
                          <strong className={m.result === 'win-away' ? 'w3-text-green' : 'w3-text-theme'}>{m.AwayTeam ? m.AwayTeam.name : 'BYE'}</strong>
                        </div>
                      )}

                      {needsApproval && isAdmin && !isFinalised ? (
                        <div className="w3-tiny" style={{ marginTop: '6px' }}>
                          <span className="w3-tag w3-round w3-blue w3-small"><i className="fa fa-clock-o w3-margin-right"></i>Awaiting Approval</span>
                          <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', marginTop: '6px' }}>
                            <button className="w3-button w3-tiny w3-green w3-round" onClick={(e) => { e.stopPropagation(); approveScore(m.id, true); }} style={{ padding: '2px 10px' }}>
                              <i className="fa fa-check"></i> Approve
                            </button>
                            <button className="w3-button w3-tiny w3-red w3-round" onClick={(e) => { e.stopPropagation(); showConfirm('Reject this score? It will be reset to pending.').then(ok => ok && approveScore(m.id, false)); }} style={{ padding: '2px 10px' }}>
                              <i className="fa fa-times"></i> Reject
                            </button>
                          </div>
                        </div>
                      ) : needsApproval && !isAdmin ? (
                        <div className="w3-center w3-tiny"><span className="w3-tag w3-round w3-blue w3-small"><i className="fa fa-clock-o w3-margin-right"></i>Awaiting Approval</span></div>
                      ) : isPending ? (
                        isOneTeamWaiting
                          ? <div className="w3-center w3-small w3-text-grey"><i className="fa fa-hourglass-half"></i> Bye this round (waiting for opponent)</div>
                          : (!isFinalised && canScore ? <div className="w3-center w3-small w3-text-theme"><i className="fa fa-pencil"></i> Tap to score</div> : null)
                      ) : !isFinalised ? (
                        <div className="w3-center w3-tiny w3-text-grey score-edit-hint"><i className="fa fa-pencil"></i> Tap to edit</div>
                      ) : (
                        <div className="w3-center w3-tiny w3-text-grey"><i className="fa fa-lock"></i> Finalised</div>
                      )}
                    </div>
                  </div>
                );
              };

              const renderRoundColumns = (roundGroups, tone) => {
                const slotHeight = 82;
                const baseGap = 16;
                const sortedGroups = roundGroups.map(group => ({
                  ...group,
                  matches: [...group.matches].sort((a, b) => ((a.bracketPosition || a.id) - (b.bracketPosition || b.id))),
                }));

                const columnPositions = [];
                for (let index = 0; index < sortedGroups.length; index++) {
                  const group = sortedGroups[index];
                  if (index === 0) {
                    columnPositions.push(group.matches.map((_, matchIndex) => matchIndex * (slotHeight + baseGap)));
                    continue;
                  }

                  const prev = columnPositions[index - 1] || [];
                  const prevCount = Math.max(prev.length, 1);
                  const currCount = Math.max(group.matches.length, 1);

                  if (currCount === prevCount) {
                    columnPositions.push(group.matches.map((_, matchIndex) => prev[Math.min(matchIndex, prev.length - 1)] || 0));
                    continue;
                  }

                  columnPositions.push(group.matches.map((_, matchIndex) => {
                    const startIndex = Math.floor((matchIndex * prevCount) / currCount);
                    const endIndex = Math.min(prevCount - 1, Math.floor((((matchIndex + 1) * prevCount) / currCount) - 1));
                    const start = prev[startIndex] || 0;
                    const end = prev[Math.max(startIndex, endIndex)] || start;
                    return Math.round((start + end) / 2);
                  }));
                }

                return (
                  <div style={{ overflowX: 'auto', paddingBottom: '6px' }}>
                    <div style={{ display: 'grid', gridAutoFlow: 'column', gridAutoColumns: 'minmax(260px, 280px)', gap: '18px', alignItems: 'start' }}>
                      {sortedGroups.map((group, index) => {
                        const groupTone = group.tone || tone;
                        const positions = columnPositions[index];
                        return (
                          <div key={group.key} style={{ minWidth: '260px' }}>
                            <div style={{ background: groupTone.headerBg, color: groupTone.headerText, border: `1px solid ${groupTone.border}`, borderRadius: '12px', padding: '10px 12px', marginBottom: '10px', fontWeight: 700 }}>
                              {group.label}
                            </div>
                            {group.matches.length === 0 ? (
                              <div className="w3-small w3-text-grey" style={{ padding: '8px 4px' }}><em>Waiting for previous round</em></div>
                            ) : (
                              <div>
                                {group.matches.map((match, matchIndex) => {
                                  const targetTop = positions[matchIndex] || 0;
                                  const prevBottom = matchIndex === 0 ? 0 : (positions[matchIndex - 1] || 0) + slotHeight;
                                  const marginTop = Math.max(matchIndex === 0 ? targetTop : targetTop - prevBottom, 8);
                                  return (
                                    <div key={match.id} style={{ marginTop: `${marginTop}px` }}>
                                      {renderScoreCard(match)}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              };

              const allRounds = Array.from(new Set(displayMatches.map(m => m.round))).sort((a, b) => a - b);
              if (allRounds.length === 0) return <p className="w3-text-grey w3-center"><em>All matches are byes</em></p>;

              const finalsMatches = displayMatches.filter(m => m.bracketType === 'finals');
              const totalFinalsRounds = finalsMatches.length > 0
                ? Math.max(...finalsMatches.map(m => m.bracketRound || 1))
                : 0;
              const finalsLabelForRoundMatches = (roundMatches) => {
                if (!roundMatches.length) return null;
                const isFinalsRound = roundMatches.every(m => m.bracketType === 'finals');
                if (!isFinalsRound) return null;
                const br = roundMatches[0].bracketRound || 1;
                if (br === totalFinalsRounds) return 'Final';
                if (br === totalFinalsRounds - 1) return 'Semi Final';
                if (br === totalFinalsRounds - 2) return 'Quarter Final';
                return 'Finals';
              };

              if (isElimination) {
                if (isScoresDE) {
                  const winners = displayMatches.filter(m => (m.bracketType === 'winners' || !m.bracketType));
                  const losers = displayMatches.filter(m => m.bracketType === 'losers');
                  const finals = displayMatches.filter(m => m.bracketType === 'grand-final' || m.bracketType === 'decider');

                  const toGroups = (list, prefix) => Array.from(new Set(list.map(m => m.bracketRound != null ? m.bracketRound : m.round))).sort((a, b) => a - b).map(round => {
                    const grouped = list.filter(m => (m.bracketRound != null ? m.bracketRound : m.round) === round);
                    const overallRound = grouped.length ? Math.min(...grouped.map(m => m.overallRound || m.round || round)) : round;
                    return {
                      key: `${prefix}-${round}`,
                      label: `${prefix} Round ${round}${overallRound !== round ? ` (Overall Round ${overallRound})` : ''}`,
                      matches: grouped,
                    };
                  });

                  const finalGroups = [];
                  const grandFinals = finals.filter(m => m.bracketType === 'grand-final');
                  const deciders = finals.filter(m => m.bracketType === 'decider');
                  if (grandFinals.length) finalGroups.push({ key: 'gf', label: 'Grand Final', matches: grandFinals, tone: { headerBg: '#eefaf0', headerText: '#2c6b31', border: '#98cf9d' } });
                  if (deciders.length) finalGroups.push({ key: 'decider', label: 'Decider', matches: deciders, tone: { headerBg: '#eefaf0', headerText: '#2c6b31', border: '#98cf9d' } });

                  const winnersPathGroups = [...toGroups(winners, 'Winners'), ...finalGroups];

                  return (
                    <>
                      <div className="w3-panel w3-pale-blue w3-round w3-small" style={{ padding: '8px 12px', marginBottom: '12px' }}>
                        <i className="fa fa-exchange w3-margin-right"></i>Elimination scores are grouped left-to-right by progression.
                      </div>
                      <div style={{ marginBottom: '16px' }}>
                        <div style={{ marginBottom: '8px', color: '#0056a8', fontWeight: 700 }}>Winners Path</div>
                        {renderRoundColumns(winnersPathGroups, { headerBg: '#eef7ff', headerText: '#0b4f88', border: '#95bfe6' })}
                      </div>
                      <div style={{ marginBottom: '16px' }}>
                        <div style={{ marginBottom: '8px', color: '#b85000', fontWeight: 700 }}>Losers Bracket</div>
                        {losers.length > 0 ? renderRoundColumns(toGroups(losers, 'Losers'), { headerBg: '#fff4e8', headerText: '#a74d00', border: '#efbf8d' }) : <div className="w3-small w3-text-grey"><em>No elimination matches yet</em></div>}
                      </div>
                    </>
                  );
                }

                const koGroups = allRounds.map(round => {
                  const roundMatches = displayMatches.filter(m => m.round === round);
                  const finalsStage = finalsLabelForRoundMatches(roundMatches);
                  return {
                    key: `round-${round}`,
                    label: `Round ${round}${finalsStage ? ` - ${finalsStage}` : ''}`,
                    matches: roundMatches,
                  };
                });
                return (
                  <>
                    <div className="w3-panel w3-pale-blue w3-round w3-small" style={{ padding: '8px 12px', marginBottom: '12px' }}>
                      <i className="fa fa-sitemap w3-margin-right"></i>Elimination scores are grouped left-to-right by round.
                    </div>
                    {renderRoundColumns(koGroups, { headerBg: '#eef7ff', headerText: '#0b4f88', border: '#95bfe6' })}
                  </>
                );
              }

              return (
                <>
                  {allRounds.map(round => {
                    const roundMatches = displayMatches.filter(m => m.round === round);
                    const finalsStage = finalsLabelForRoundMatches(roundMatches);
                    const roundLabel = isScoresDE && roundMatches[0]
                      ? deRoundLabel(roundMatches[0])
                      : `Round ${round}${finalsStage ? ` - ${finalsStage}` : ''}`;
                    return (
                    <div key={round} className="w3-margin-bottom">
                      {/* Was a filled bar the full width of the page to carry
                          two words. A label does the same job. */}
                      <h5 className="gs-section-label" style={{ display: 'block', marginBottom: '10px' }}>{roundLabel}</h5>
                      {/* Pending for this round */}
                      {pending.filter(m => m.round === round).length > 0 && (
                        <div className="score-grid">
                          {pending.filter(m => m.round === round).map(m => {
                            const canScore = (isAdmin && !isFinalised) || (!isAdmin && !isFinalised && isPlayerInMatch(m));
                            return (
                            <div key={m.id} className="score-card"
                              onClick={() => setScoringMatch(m)}
                              style={{ cursor: 'pointer', opacity: isFinalised ? 0.7 : 1 }}>
                              <div className="score-meta">R{m.round} &middot; Rink {m.rink || '?'}</div>
                              <div className="score-line"><span className="score-name">{m.HomeTeam ? m.HomeTeam.name : 'BYE'}</span><span className="score-num">&ndash;</span></div>
                              <div className="score-line"><span className="score-name">{m.AwayTeam ? m.AwayTeam.name : 'BYE'}</span><span className="score-num">&ndash;</span></div>
                              {!isFinalised
                                ? (canScore ? <div className="score-cta">Tap to score</div> : null)
                                : <div className="score-cta-muted"><i className="fa fa-lock"></i> Finalised</div>}
                            </div>
                            );
                          })}
                        </div>
                      )}
                      {/* Completed for this round */}
                      {done.filter(m => m.round === round).length > 0 && (
                        <div className="score-grid">
                          {done.filter(m => m.round === round).map(m => {
                            const needsApproval = m.completed && !m.scoreApproved && m.scoreSubmittedBy === 'player';
                            // The whole card used to be washed pale green, pale
                            // yellow or pale blue depending on outcome. The
                            // winner is marked on its own row now, so a played
                            // match reads as a scoreboard rather than a status.
                            return (
                            <div key={m.id} className="score-card"
                              onClick={() => setScoringMatch(m)}
                              style={{ cursor: 'pointer' }}>
                              <div>
                                <div className="score-meta">R{m.round} &middot; Rink {m.rink || '?'}</div>
                                {m.result === 'not-played' ? (
                                  <div style={{ margin: '6px 0' }}>
                                    <span className="w3-tag w3-round w3-orange w3-small"><i className="fa fa-ban w3-margin-right"></i>Not Played</span>
                                  </div>
                                ) : (m.homeScore === 0 && m.awayScore === 0 && (m.result === 'win-home' || m.result === 'win-away')) ? (
                                  <div style={{ margin: '6px 0' }}>
                                    <span className="w3-tag w3-round w3-deep-orange w3-small"><i className="fa fa-flag w3-margin-right"></i>Forfeit &mdash; {m.result === 'win-home' ? (m.HomeTeam ? m.HomeTeam.name : 'Home') : (m.AwayTeam ? m.AwayTeam.name : 'Away')} wins</span>
                                  </div>
                                ) : (() => {
                                  const sc = matchScoreDisplay(m, selectedComp);
                                  return (
                                <>
                                  <div className={`score-line ${m.result === 'win-home' ? 'score-win' : ''}`}>
                                    <span className="score-name">{m.HomeTeam ? m.HomeTeam.name : '-'}</span>
                                    <span className="score-num">{sc.home}</span>
                                  </div>
                                  <div className={`score-line ${m.result === 'win-away' ? 'score-win' : ''}`}>
                                    <span className="score-name">{m.AwayTeam ? m.AwayTeam.name : '-'}</span>
                                    <span className="score-num">{sc.away}</span>
                                  </div>
                                  {sc.detail && <div className="score-meta">{sc.detail}</div>}
                                </>
                                  );
                                })()}
                                {needsApproval && isAdmin && !isFinalised ? (
                                  <div className="w3-tiny" style={{ marginTop: '4px' }}>
                                    <span className="w3-tag w3-round w3-blue w3-small" style={{ marginBottom: '4px' }}><i className="fa fa-clock-o w3-margin-right"></i>Awaiting Approval</span>
                                    <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', marginTop: '4px' }}>
                                      <button className="w3-button w3-tiny w3-green w3-round" onClick={(e) => { e.stopPropagation(); approveScore(m.id, true); }} style={{ padding: '2px 10px' }}>
                                        <i className="fa fa-check"></i> Approve
                                      </button>
                                      <button className="w3-button w3-tiny w3-red w3-round" onClick={(e) => { e.stopPropagation(); showConfirm('Reject this score? It will be reset to pending.').then(ok => ok && approveScore(m.id, false)); }} style={{ padding: '2px 10px' }}>
                                        <i className="fa fa-times"></i> Reject
                                      </button>
                                    </div>
                                  </div>
                                ) : needsApproval && !isAdmin ? (
                                  <div className="w3-tiny"><span className="w3-tag w3-round w3-blue w3-small"><i className="fa fa-clock-o w3-margin-right"></i>Awaiting Approval</span></div>
                                ) : !isFinalised
                                  ? <div className="w3-tiny w3-text-grey score-edit-hint"><i className="fa fa-pencil"></i> Tap to edit</div>
                                  : <div className="w3-tiny w3-text-grey"><i className="fa fa-lock"></i> Finalised</div>}
                              </div>
                            </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );})}
                </>
              );
            })()}
          </>
        )}

        {/* Nav */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '16px', paddingBottom: '4px' }}>
          <button className="w3-button w3-theme-l4 w3-round" onClick={() => setActiveStep('fixtures')}>
            <i className="fa fa-arrow-left w3-margin-right"></i>Back
          </button>
          <button className="w3-button w3-theme w3-round-large"
            onClick={() => { setActiveStep('leaderboard'); refreshComp(); }} disabled={completedMatches === 0}>
            View Results <TrophySvg size={14} style={{ marginLeft: '8px' }} />
          </button>
        </div>
      </div>
    </div>
  );
}

export default CompetitionScores;
